import type { Vec3 } from '../../geo/types';

/** An axis-aligned box. */
export interface Box {
  readonly min: Vec3;
  readonly max: Vec3;
}

export type Face = readonly [number, number, number];

/** A closed triangle mesh, wound so that each face turns its front out of the solid, with the
    surface's unit normal at every point. */
export interface SurfaceMesh {
  readonly points: readonly Vec3[];
  readonly normals: readonly Vec3[];
  readonly faces: readonly Face[];
}

/** A distance-like field, negative inside the solid, with its gradient. */
export interface ImplicitSurface {
  readonly value: (p: Vec3) => number;
  /** A value of the field's sign, equal to the field wherever the surface may be near. */
  readonly sample: (p: Vec3) => number;
  readonly withGradient: (p: Vec3) => readonly [number, Vec3];
}

/* From a cell's mean crossing, a distance-like field's zero set lies a few Newton steps away. */
const NEWTON_STEPS = 2;

/* The grid's corners are indexed i + j * strides[1] + k * strides[2], and each cell by its lowest
   corner. */
interface Grid {
  readonly origin: Vec3;
  readonly cell: number;
  readonly size: readonly [number, number, number];
  readonly strides: readonly [number, number, number];
  /** The field at each corner sampled so far, NaN at the others. */
  readonly values: Float64Array;
}

/** The surface reaches the box it was to be meshed in. */
export class SurfaceLeavesBox extends Error {
  constructor() {
    super('Surface net: the surface leaves its box');
    this.name = 'SurfaceLeavesBox';
  }
}

function gridOver(box: Box, cell: number): Grid {
  const size = [0, 1, 2].map((a) => Math.ceil((box.max[a]! - box.min[a]!) / cell)) as [
    number,
    number,
    number,
  ];
  const strides: [number, number, number] = [1, size[0] + 1, (size[0] + 1) * (size[1] + 1)];
  const values = new Float64Array(strides[2] * (size[2] + 1)).fill(NaN);
  return { origin: box.min, cell, size, strides, values };
}

/* The grid coordinates of the corner, or the cell, of this index. */
function coordsOf(grid: Grid, index: number): [number, number, number] {
  const k = Math.floor(index / grid.strides[2]);
  const rest = index - k * grid.strides[2];
  const j = Math.floor(rest / grid.strides[1]);
  return [rest - j * grid.strides[1], j, k];
}

/* The field at a corner, sampled the first time a cell there is looked at: the net looks only at
   the cells about the surface. */
function valueAt(surface: ImplicitSurface, grid: Grid, index: number): number {
  const known = grid.values[index]!;
  if (!Number.isNaN(known)) return known;
  const [i, j, k] = coordsOf(grid, index);
  const value = surface.sample(cornerAt(grid.origin, grid.cell, i, j, k));
  const [nx, ny, nz] = grid.size;
  const onBoundary = i === 0 || j === 0 || k === 0 || i === nx || j === ny || k === nz;
  if (onBoundary && value <= 0) throw new SurfaceLeavesBox();
  grid.values[index] = value;
  return value;
}

function cornerAt(origin: Vec3, cell: number, i: number, j: number, k: number): Vec3 {
  return [origin[0] + i * cell, origin[1] + j * cell, origin[2] + k * cell];
}

const CUBE: readonly Vec3[] = [0, 1, 2, 3, 4, 5, 6, 7].map((c) => [c & 1, (c >> 1) & 1, c >> 2]);
const CUBE_EDGES: readonly (readonly [number, number])[] = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

/* Offsets of a cell's corners in the grid's values, from its lowest corner. */
function cornerOffsets(grid: Grid): Int32Array {
  return Int32Array.from(CUBE.map(([a, b, c]) => a + b * grid.strides[1] + c * grid.strides[2]));
}

/* The cube's corners and its edges' ends flat, for the loop that visits every cell. */
const CORNERS = Int8Array.from(CUBE.flat());
const EDGE_ENDS = Int8Array.from(CUBE_EDGES.flat());

/* On one axis, in cells from a cell's lowest corner, the point a share t of the way along the edge
   from its corner a to its corner b. */
function alongEdge(a: number, b: number, axis: number, t: number): number {
  const from = CORNERS[3 * a + axis]!;
  return from + t * (CORNERS[3 * b + axis]! - from);
}

/* The corners of a cell inside the surface, as the bits of a mask in CUBE's order. */
function insideMask(
  surface: ImplicitSurface,
  grid: Grid,
  offsets: Int32Array,
  cell: number
): number {
  let mask = 0;
  for (let c = 0; c < 8; c++) if (valueAt(surface, grid, cell + offsets[c]!) < 0) mask |= 1 << c;
  return mask;
}

const ALL_INSIDE = 0xff;

/* The faces of a cell, low then high on each axis: the mask of each one's four corners, its axis,
   and the way to the cell beyond it. */
const FACE_AXIS = Int8Array.from([0, 0, 1, 1, 2, 2]);
const FACE_STEP = Int8Array.from([-1, 1, -1, 1, -1, 1]);
const FACE_CORNERS = Int32Array.from(FACE_AXIS, (axis, f) =>
  CUBE.reduce((mask, corner, c) => (corner[axis] === (f & 1) ? mask | (1 << c) : mask), 0)
);

/* The cell the surface crosses first going up from a seed inside it, or null for a seed that is
   not. A surface that runs out of the top of the box stops the climb. */
function crossedAbove(
  surface: ImplicitSurface,
  grid: Grid,
  offsets: Int32Array,
  seed: Vec3
): number | null {
  const [i, j0, k] = [0, 1, 2].map((a) => Math.floor((seed[a]! - grid.origin[a]!) / grid.cell));
  if ([i!, j0!, k!].some((v, a) => v < 0 || v >= grid.size[a]!)) return null;
  for (let j = j0!; j < grid.size[1]; j++) {
    const cell = i! + j * grid.strides[1] + k! * grid.strides[2];
    const mask = insideMask(surface, grid, offsets, cell);
    if (mask === 0) return null;
    if (mask !== ALL_INSIDE) return cell;
  }
  return null;
}

/* The cells beyond those faces of a crossed cell that the surface crosses too, inside the grid. */
function crossedBeside(grid: Grid, cell: number, mask: number): number[] {
  const at = coordsOf(grid, cell);
  const beside: number[] = [];
  for (let f = 0; f < 6; f++) {
    const axis = FACE_AXIS[f]!;
    const step = FACE_STEP[f]!;
    const corners = FACE_CORNERS[f]!;
    const on = mask & corners;
    const next = at[axis]! + step;
    if (on === 0 || on === corners || next < 0 || next >= grid.size[axis]!) continue;
    beside.push(cell + step * grid.strides[axis]!);
  }
  return beside;
}

/* The cells the surface crosses, found going up from each seed to the surface and then from each
   crossed cell across every face of it the surface crosses too, in the grid's order. */
function crossedCells(
  surface: ImplicitSurface,
  grid: Grid,
  offsets: Int32Array,
  seeds: readonly Vec3[]
): Int32Array {
  const seen = new Uint8Array(grid.values.length);
  const stack: number[] = [];
  const visit = (cell: number): void => {
    if (seen[cell] === 1) return;
    seen[cell] = 1;
    stack.push(cell);
  };
  seeds.forEach((seed) => {
    const cell = crossedAbove(surface, grid, offsets, seed);
    if (cell !== null) visit(cell);
  });
  const cells: number[] = [];
  while (stack.length > 0) {
    const cell = stack.pop()!;
    cells.push(cell);
    crossedBeside(grid, cell, insideMask(surface, grid, offsets, cell)).forEach(visit);
  }
  return Int32Array.from(cells).sort();
}

/* The mean of the points where the field crosses zero along the edges of a cell it crosses. */
function cellCrossing(grid: Grid, offsets: Int32Array, cell: number): Vec3 {
  const values = grid.values;
  let [x, y, z, n] = [0, 0, 0, 0];
  for (let e = 0; e < EDGE_ENDS.length; e += 2) {
    const a = EDGE_ENDS[e]!;
    const b = EDGE_ENDS[e + 1]!;
    const va = values[cell + offsets[a]!]!;
    const vb = values[cell + offsets[b]!]!;
    if (va < 0 === vb < 0) continue;
    const t = va / (va - vb);
    x += alongEdge(a, b, 0, t);
    y += alongEdge(a, b, 1, t);
    z += alongEdge(a, b, 2, t);
    n += 1;
  }
  const [i, j, k] = coordsOf(grid, cell);
  return cornerAt(grid.origin, grid.cell, i + x / n, j + y / n, k + z / n);
}

/* The last step moves a point by a thousandth of a cell or so, which turns the gradient by far
   less than a degree: the gradient it was taken along is the surface normal at its end. */
function ontoSurface(surface: ImplicitSurface, start: Vec3): { point: Vec3; normal: Vec3 } {
  let [point, normal] = [start, [0, 0, 0] as Vec3];
  for (let step = 0; step < NEWTON_STEPS; step++) {
    const [value, g] = surface.withGradient(point);
    const g2 = g[0] * g[0] + g[1] * g[1] + g[2] * g[2];
    if (g2 === 0) break;
    const length = Math.sqrt(g2);
    normal = [g[0] / length, g[1] / length, g[2] / length];
    point = [
      point[0] - (value * g[0]) / g2,
      point[1] - (value * g[1]) / g2,
      point[2] - (value * g[2]) / g2,
    ];
  }
  return { point, normal };
}

function cellVertices(
  surface: ImplicitSurface,
  grid: Grid,
  offsets: Int32Array,
  cells: Int32Array
): { points: Vec3[]; normals: Vec3[]; vertexOf: Int32Array } {
  const points: Vec3[] = [];
  const normals: Vec3[] = [];
  const vertexOf = new Int32Array(grid.values.length).fill(-1);
  for (const cell of cells) {
    vertexOf[cell] = points.length;
    const { point, normal } = ontoSurface(surface, cellCrossing(grid, offsets, cell));
    points.push(point);
    normals.push(normal);
  }
  return { points, normals, vertexOf };
}

/* For a grid edge along each axis, the offsets of the four cells around it, in the order that
   winds their quad counter-clockwise seen from the edge's far end. */
const AROUND: readonly (readonly Vec3[])[] = [
  [
    [0, -1, -1],
    [0, 0, -1],
    [0, 0, 0],
    [0, -1, 0],
  ],
  [
    [-1, 0, -1],
    [-1, 0, 0],
    [0, 0, 0],
    [0, 0, -1],
  ],
  [
    [-1, -1, 0],
    [0, -1, 0],
    [0, 0, 0],
    [-1, 0, 0],
  ],
];

function distanceSq(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

/* The two triangles of the quad a, b, c, d, split along its shorter diagonal. */
function pushSplit(faces: Face[], points: readonly Vec3[], quad: readonly number[]): void {
  const [a, b, c, d] = quad as [number, number, number, number];
  if (distanceSq(points[a]!, points[c]!) <= distanceSq(points[b]!, points[d]!)) {
    faces.push([a, b, c], [a, c, d]);
  } else {
    faces.push([a, b, d], [b, c, d]);
  }
}

/* The faces across every grid edge along the axis that the surface crosses, each met at the cell
   it starts at, which the surface crosses too. Edges on the box's faces join corners outside the
   surface; along its own axis an edge may start on the box, with the four cells around it inside. */
function facesAcross(
  grid: Grid,
  vertexOf: Int32Array,
  points: readonly Vec3[],
  cells: Int32Array,
  axis: number
): Face[] {
  const [s0, s1, s2] = grid.strides;
  const around = AROUND[axis]!.map(([a, b, c]) => a * s0 + b * s1 + c * s2);
  const stride = grid.strides[axis]!;
  const faces: Face[] = [];
  const quad = [0, 0, 0, 0];
  for (const cell of cells) {
    const at = coordsOf(grid, cell);
    if ((axis !== 0 && at[0] === 0) || (axis !== 1 && at[1] === 0) || (axis !== 2 && at[2] === 0)) {
      continue;
    }
    const inside = grid.values[cell]! < 0;
    if (inside === grid.values[cell + stride]! < 0) continue;
    for (let q = 0; q < 4; q++) quad[inside ? q : 3 - q] = vertexOf[cell + around[q]!]!;
    if (quad[0]! >= 0 && quad[1]! >= 0 && quad[2]! >= 0 && quad[3]! >= 0) {
      pushSplit(faces, points, quad);
    }
  }
  return faces;
}

/* Where the surface grazes a grid face, round a crown or across a neck, the cells on either side
   of it cross the surface at almost the same points: their vertices land a hair apart and the
   faces between them fold inside out. The ends of every face edge shorter than this share of a
   cell are welded into one vertex, and the faces that collapse with them dropped. */
const WELD = 0.1;

function weldRoots(mesh: SurfaceMesh, cell: number): Int32Array {
  const parent = new Int32Array(mesh.points.length).map((_, v) => v);
  const root = (v: number): number => {
    let r = v;
    while (parent[r] !== r) r = parent[r]!;
    return r;
  };
  const limit = (WELD * cell) ** 2;
  for (const face of mesh.faces) {
    for (let k = 0; k < 3; k++) {
      const [a, b] = [face[k]!, face[(k + 1) % 3]!];
      if (distanceSq(mesh.points[a]!, mesh.points[b]!) < limit) parent[root(b)] = root(a);
    }
  }
  return parent.map((_, v) => root(v));
}

function welded(mesh: SurfaceMesh, cell: number): SurfaceMesh {
  const roots = weldRoots(mesh, cell);
  const faces = mesh.faces
    .map(([a, b, c]): Face => [roots[a]!, roots[b]!, roots[c]!])
    .filter(([a, b, c]) => a !== b && b !== c && c !== a);
  const index = new Int32Array(mesh.points.length).fill(-1);
  const kept: number[] = [];
  for (const face of faces) {
    for (const v of face) {
      if (index[v] !== -1) continue;
      index[v] = kept.length;
      kept.push(v);
    }
  }
  return {
    points: kept.map((v) => mesh.points[v]!),
    normals: kept.map((v) => mesh.normals[v]!),
    faces: faces.map(([a, b, c]): Face => [index[a]!, index[b]!, index[c]!]),
  };
}

/**
 * Naive surface net of the field's zero set (negative inside) in the box, sampled every `cell`:
 * a vertex in every cell the surface crosses, moved onto the surface, and a quad across every
 * grid edge the surface crosses, with vertices a hair apart welded. The net follows the surface
 * from cell to cell from where it meets it going up from each seed, a point inside the solid: it
 * meshes the surfaces met so, and samples the field only about them. The surface must lie inside
 * the box.
 */
export function surfaceNet(
  surface: ImplicitSurface,
  box: Box,
  cell: number,
  seeds: readonly Vec3[]
): SurfaceMesh {
  const grid = gridOver(box, cell);
  const offsets = cornerOffsets(grid);
  const cells = crossedCells(surface, grid, offsets, seeds);
  const { points, normals, vertexOf } = cellVertices(surface, grid, offsets, cells);
  const faces = [0, 1, 2].flatMap((axis) => facesAcross(grid, vertexOf, points, cells, axis));
  return welded({ points, normals, faces }, cell);
}
